import { useShallow } from 'zustand/react/shallow'

import { Source } from '@ui/__generated__/graphql'
import { ApolloErrorMessage } from '@ui/components/core/ApolloErrorMessage'
import { CardStack } from '@ui/components/core/CardStack'
import { Spinner } from '@ui/components/loaders'
import { sourceTypeToLogoAssetSlug } from '@ui/constants/sources'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import { GetSourcesQuery } from '@ui/loaders/sources'
import { useSaveUpdateStatusStore } from '@ui/stores'

export function AssignSourcesWizard() {
  const { assignedSourceIds, setAssignedSourceIds } = useSaveUpdateStatusStore(
    useShallow((state) => ({
      assignedSourceIds: state.assignedSourceIds,
      setAssignedSourceIds: state.setAssignedSourceIds,
    }))
  )

  const { data, loading, error } = GetSourcesQuery()

  const logoPathBySlug = useLogoPathBySlug(
    data?.sources.map((source: Source) => sourceTypeToLogoAssetSlug[source.type])
  )

  const onSelect = (source: Source) => {
    const updatedAssignedSourceIds = new Set(assignedSourceIds)

    if (updatedAssignedSourceIds.has(source.id)) {
      updatedAssignedSourceIds.delete(source.id)
    } else {
      updatedAssignedSourceIds.add(source.id)
    }

    setAssignedSourceIds({ assignedSourceIds: updatedAssignedSourceIds, updatePipelineModified: true })
  }

  if (error) {
    return <ApolloErrorMessage error={error} />
  }

  const sources: Source[] = data?.sources || []

  return (
    <div>
      <div className="mb-6 border-b border-gray-200 pb-5 pr-4 sm:pr-6">
        <div className="text-2xl font-medium leading-6 text-gray-900">Assign sources to this pipeline</div>
      </div>
      <div className="overflow-hidden">
        {loading ? (
          <Spinner />
        ) : (
          <CardStack
            items={sources}
            onSelect={onSelect}
            isSelected={(source) => assignedSourceIds.has(source.id)}
            getImage={(source) => {
              const slug = sourceTypeToLogoAssetSlug[source.type]
              const logoPath = logoPathBySlug[slug]

              return {
                src: logoPath,
                alt: source.type,
              }
            }}
          />
        )}
      </div>
    </div>
  )
}
